import { API_BASE_URL, API_ENDPOINTS, ERROR_MESSAGES } from '../constants';

/**
 * Generic API request function with error handling
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} - API response
 */
export const apiRequest = async (endpoint, options = {}) => {
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    // console.log('🌐 Making API request to:', url);
    //console.log('🔧 API_BASE_URL:', API_BASE_URL);
    
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    //console.log('📡 Response status:', response.status);
    //console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const responseText = await response.text();
      //console.log('❌ Error response text:', responseText);
      
      // Try to parse as JSON, but handle HTML responses
      let errorData = {};
      try {
        errorData = JSON.parse(responseText);
      } catch (parseError) {
        //console.log('⚠️ Response is not JSON, likely HTML error page');
        throw new Error(`Server returned HTML instead of JSON. Status: ${response.status}. URL: ${url}`);
      }
      
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const responseText = await response.text();
    //console.log('✅ Response text:', responseText);
    
    // Try to parse as JSON
    try {
      return JSON.parse(responseText);
    } catch (parseError) {
      //  console.log('❌ Failed to parse response as JSON:', parseError);
      throw new Error(`Server returned invalid JSON. Response: ${responseText.substring(0, 100)}...`);
    }
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};

/**
 * Register a new user
 * @param {string} email - User email
 * @returns {Promise<Object>} - Registration response
 */
export const registerUser = async (email) => {
  return apiRequest(API_ENDPOINTS.USERS.REGISTER, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
};

/**
 * Select a prize for the user's spin (secure - prize determined on backend)
 * @param {string} email - User email
 * @returns {Promise<Object>} - Prize selection response with prize and spinId
 */
export const selectPrizeForSpin = async (email) => {
  return apiRequest('/api/users/select-prize', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
};

/**
 * Record a user's spin with the predetermined prize
 * @param {string} email - User email
 * @param {string} spinId - Unique spin session ID
 * @returns {Promise<Object>} - Spin response
 */
export const recordSpin = async (email, spinId) => {
  return apiRequest(API_ENDPOINTS.USERS.SPIN, {
    method: 'POST',
    body: JSON.stringify({ 
      email, 
      spinId
    }),
  });
};

/**
 * Check user's spin status
 * @param {string} email - User email
 * @returns {Promise<Object>} - Spin status response
 */
export const checkSpinStatus = async (email) => {
  return apiRequest(`${API_ENDPOINTS.USERS.SPIN_STATUS}/${encodeURIComponent(email)}`);
};

/**
 * Fetch available prizes
 * @returns {Promise<Array>} - Array of prizes
 */
export const fetchPrizes = async () => {
  const response = await apiRequest(API_ENDPOINTS.PRIZES.LIST);
  
  // Handle different response formats
  if (Array.isArray(response)) {
    return response;
  }
  
  if (response?.success && Array.isArray(response.data)) {
    return response.data;
  }
  
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  
  if (Array.isArray(response?.prizes)) {
          return response.prizes;
    }
    
    // console.warn('Unexpected prizes API response format:', response);
    return [];
};

/**
 * Fetch available prizes only (quantity > 0 and isActive = true)
 * @returns {Promise<Array>} - Array of available prizes
 */
export const fetchAvailablePrizes = async () => {
  const response = await apiRequest(API_ENDPOINTS.PRIZES.AVAILABLE);
  
  // Handle different response formats
  if (Array.isArray(response)) {
    return response;
  }
  
  if (response?.success && Array.isArray(response.data)) {
    return response.data;
  }
  
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  
  if (Array.isArray(response?.prizes)) {
    return response.prizes;
  }
  
  // console.warn('Unexpected available prizes API response format:', response);
  return [];
};

/**
 * Seed prizes (for development)
 * @returns {Promise<Object>} - Seeding response
 */
export const seedPrizes = async () => {
  return apiRequest(API_ENDPOINTS.PRIZES.SEED, {
    method: 'POST',
  });
};
